import { recordEvent, setMonitoringUser } from 'src/utilities/monitoring.js';
import type { ActionCreator, ThunkAction } from '@reduxjs/toolkit';
import { t } from 'src/i18n';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import type {
  CompleteOrganizationViewModel,
  CreateOrUpdateAssetRequest,
  CreateOrUpdateContractRequest,
  CreateOrUpdateEmployeeRequest,
  CreateOrUpdatePartnerRequest,
  CreateOrUpdateRiskAssessmentRequest,
  EventOccurrenceViewModel,
  FunctionTaskUpdateMessage,
  MeetingOccurrenceViewModel,
  OrganizationReference,
  PageGroupViewModel,
  ParticipantState,
  ReferenceViewModel,
  SendMessageCommand,
  SendShareVacationCommand,
  StopShareVacationCommand,
  TopicViewModel,
  CompetenceViewModel,
  TutorialViewModel,
  TutorialRegistration,
  TutorialTemplateContent,
} from '@trinnvis/dabih-api-client';
import { EmployeeUpdateMessageGenderEnum } from '@trinnvis/dabih-api-client';
import { BASE_PATH } from '../config.js';
import type {
  DAction,
  DeleteItemAction,
  EntityViewModel,
  LoadedCachedOrganizationAction,
  LoadedErrorAction,
  LoadedEventOccurrencesAction,
  LoadedMeetingOccurrencesAction,
  LoadedOrganizationAction,
  LoadedPageGroupsAction,
  LoadedReferencesAction,
  LoadedTopicsAction,
  LoadedTutorialsAction,
  LoadedCompetenceAction,
  LoadedScreensAction,
  LoadingOrganizationAction,
  OrganizationState,
  PresenceState,
  State,
  UpdateCalendarSingleUserViewAction,
  UpdateCalendarWeekAction,
  UpdateCopilotOpenAction,
  UpdateChatOpenAction,
  UpdateChatModeAction,
  UpdateChatExpandedAction,
  UpdateMatrixReadyAction,
  UpdateMatrixRoomsAction,
  UpdateMatrixCurrentRoomAction,
  UpdateMatrixUnreadCountAction,
  UpdateUserPresenceAction,
  UpdateCurrentEditLevelAction,
  UpdateCurrentHelpPageAction,
  UpdateCurrentSideContentTemplateIdAction,
  UpdateCurrentTutorialParticipantIdAction,
  UpdateCurrentTutorialParticipantStateAction,
  CacheTutorialContentAction,
  UpdateHelpViewerOpenAction,
  UpdateItemAction,
  UpdatePathAction,
  UpdatePopupOpenAction,
  UpdateRecognizedTextAction,
  UpdateSelectedStartTaskAction,
  UpdateSideContentOpenAction,
  UpdateTutorialViewerOpenAction,
  UpdateUserAction,
  UpdateUserPropertiesAction,
  User,
  Screen,
} from '../types.js';
import { Buffer } from 'buffer/index.js';
import { getOrganization } from '../selectors/organization/getOrganization';
import type { NotifyErrorCallback } from './notifyErrorCallback';
import type { ThunkResult } from './thunkResult';
import type { AlertCallback } from './alertCallback';
import type { ApiClient } from '../backend';
import { getBasePath } from '../backend';
import { cache } from '../index';
import { getScreensForOrganization, fetchScreen } from '../signage';
import { getRefreshToken, isRefreshTokenExpired } from 'src/store/oauth-browser';
import { setPersistedAuthIdentity } from 'src/auth/auth-storage.js';

let notifyError: NotifyErrorCallback = () => {
  // empty placeholder
};

let alert: AlertCallback = () => {
  // empty placeholder
};

const saveErrorMessage = (): string => t('actions.saveError');

export function initActions(notifyErrorCallback: NotifyErrorCallback, alertCallback: AlertCallback): void {
  notifyError = notifyErrorCallback;
  alert = alertCallback;
}

class ResponseError extends Error {
  // Set name so monitoring classifies these as "ResponseError" (not generic "Error"),
  // matching the generated API client's ResponseError. Without this, the 401/403/404
  // filter in app-root.ts onError never matches and these expected user errors are reported.
  override name = 'ResponseError';
  response: any;
  status?: number;
}

export const updatePath: ActionCreator<UpdatePathAction> = (path: string, queryParams: Record<string, string>) => {
  return {
    type: 'UPDATE_PATH',
    path: path,
    queryParams: queryParams,
  };
};

export const deleteItemAction: ActionCreator<DeleteItemAction> = (message: { path: string }) => {
  return {
    type: 'DELETE_ITEM',
    message: message,
  };
};

export const updateItemAction: ActionCreator<UpdateItemAction> = (message: { path: string; item: EntityViewModel }) => {
  return {
    type: 'UPDATE_ITEM',
    message: message,
  };
};

export const updateUserAction: ActionCreator<UpdateUserAction> = (user?: User) => {
  return {
    type: 'UPDATE_USER',
    user: user,
  };
};

export const updateWeekStart: ActionCreator<UpdateCalendarWeekAction> = (weekStart: string) => {
  return {
    type: 'UPDATE_CALENDAR_WEEK',
    weekStart: weekStart,
  };
};

export const updatePopupOpen: ActionCreator<UpdatePopupOpenAction> = (value: boolean) => {
  return {
    type: 'UPDATE_POPUP_OPEN',
    popupOpen: value,
  };
};

export const updateCurrentEditLevel: ActionCreator<UpdateCurrentEditLevelAction> = (value: number) => {
  return {
    type: 'UPDATE_CURRENT_EDIT_LEVEL',
    currentEditLevel: value,
  };
};

export const updateHelpViewerOpen: ActionCreator<UpdateHelpViewerOpenAction> = (value: boolean) => {
  return {
    type: 'UPDATE_HELP_VIEWER_OPEN',
    helpViewerOpen: value,
  };
};

export const updateSideContentOpen: ActionCreator<UpdateSideContentOpenAction> = (value: boolean) => {
  return {
    type: 'UPDATE_SIDE_CONTENT_OPEN',
    sideContentOpen: value,
  };
};

export const updateCopilotOpen: ActionCreator<UpdateCopilotOpenAction> = (value: boolean) => {
  return {
    type: 'UPDATE_COPILOT_OPEN',
    copilotOpen: value,
  };
};

export const updateChatOpen: ActionCreator<UpdateChatOpenAction> = (value: boolean) => {
  return {
    type: 'UPDATE_CHAT_OPEN',
    chatOpen: value,
  };
};

export const updateChatMode: ActionCreator<UpdateChatModeAction> = (value: 'ai' | 'user') => {
  return {
    type: 'UPDATE_CHAT_MODE',
    chatMode: value,
  };
};

export const updateChatExpanded: ActionCreator<UpdateChatExpandedAction> = (value: boolean) => {
  return {
    type: 'UPDATE_CHAT_EXPANDED',
    chatExpanded: value,
  };
};

export const updateMatrixReady: ActionCreator<UpdateMatrixReadyAction> = (value: boolean) => {
  return {
    type: 'UPDATE_MATRIX_READY',
    matrixReady: value,
  };
};

export const updateMatrixRooms: ActionCreator<UpdateMatrixRoomsAction> = (rooms: any[]) => {
  return {
    type: 'UPDATE_MATRIX_ROOMS',
    matrixRooms: rooms,
  };
};

export const updateMatrixCurrentRoom: ActionCreator<UpdateMatrixCurrentRoomAction> = (roomId: string | null) => {
  return {
    type: 'UPDATE_MATRIX_CURRENT_ROOM',
    matrixCurrentRoomId: roomId,
  };
};

export const updateMatrixUnreadCount: ActionCreator<UpdateMatrixUnreadCountAction> = (count: number) => {
  return {
    type: 'UPDATE_MATRIX_UNREAD_COUNT',
    matrixUnreadCount: count,
  };
};

export const updateUserPresence = (
  userId: string,
  presenceState: Omit<PresenceState, 'lastUpdateTime'>,
): UpdateUserPresenceAction => {
  return {
    type: 'UPDATE_USER_PRESENCE',
    userId,
    presenceState: {
      ...presenceState,
      lastUpdateTime: Date.now(),
    },
  };
};

export const updateTutorialViewerOpen: ActionCreator<UpdateTutorialViewerOpenAction> = (value: boolean) => {
  return {
    type: 'UPDATE_TUTORIAL_VIEWER_OPEN',
    tutorialViewerOpen: value,
  };
};

export const updateCurrentTutorialParticipantId: ActionCreator<UpdateCurrentTutorialParticipantIdAction> = (
  value: string | undefined,
) => {
  return {
    type: 'UPDATE_CURRENT_TUTORIAL_PARTICIPANT_ID',
    currentTutorialParticipantId: value,
  };
};

export const updateCurrentTutorialParticipantState: ActionCreator<UpdateCurrentTutorialParticipantStateAction> = (
  state: ParticipantState | undefined,
) => {
  return {
    type: 'UPDATE_CURRENT_TUTORIAL_PARTICIPANT_STATE',
    state,
  };
};

export const cacheTutorialContent: ActionCreator<CacheTutorialContentAction> = (
  key: string,
  content: TutorialTemplateContent,
) => {
  console.log('[cacheTutorialContent] Caching content:', {
    key,
    partsCount: content.parts?.length,
    partsWithTests: content.parts?.filter((p) => p.test && p.test.length > 0).length || 0,
    firstPartHasTest: !!content.parts?.[0]?.test,
    firstPartTestCount: content.parts?.[0]?.test?.length || 0,
    rawContent: content,
  });

  return {
    type: 'CACHE_TUTORIAL_CONTENT',
    key,
    content,
  };
};

export const updateCurrentHelpPage: ActionCreator<UpdateCurrentHelpPageAction> = (currentHelpPage: string) => {
  return {
    type: 'UPDATE_CURRENT_HELP_PAGE',
    currentHelpPage: currentHelpPage,
  };
};

export const updateRecognizedText: ActionCreator<UpdateRecognizedTextAction> = (text?: string) => {
  return {
    type: 'UPDATE_RECOGNIZED_TEXT',
    text: text,
  };
};

export const updateCurrentSideContentTemplateId: ActionCreator<UpdateCurrentSideContentTemplateIdAction> = (
  currentSideContentTemplateId: number,
) => {
  return {
    type: 'UPDATE_CURRENT_SIDE_CONTENT_TEMPLATE_ID',
    currentSideContentTemplateId,
  };
};

export const updateCalendarSingleUserView: ActionCreator<UpdateCalendarSingleUserViewAction> = (value: boolean) => {
  return {
    type: 'UPDATE_CALENDAR_SINGLE_USER_VIEW',
    singleUserView: value,
  };
};

export const updateSelectedStartTask: ActionCreator<UpdateSelectedStartTaskAction> = (selectedStartTask: string) => {
  return {
    type: 'UPDATE_SELECTED_START_TASK',
    selectedStartTask: selectedStartTask,
  };
};

export const updateUserPropertiesAction: ActionCreator<UpdateUserPropertiesAction> = (
  properties: Record<string, unknown>,
) => {
  return {
    type: 'UPDATE_USER_PROPERTIES',
    properties: properties,
  };
};

export function checkStatus(response: { status: number; statusText?: string }): any {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    // Provide descriptive error message with fallback for empty statusText
    const statusText = response.statusText && response.statusText.trim() !== '' ? response.statusText : 'HTTP error';
    const errorMessage = `${statusText} (status: ${response.status})`;
    const error = new ResponseError(errorMessage);
    error.response = response;
    error.status = response.status;
    throw error;
  }
}

export function onFetchRejected(message: string): (error: Error) => void {
  return (error: Error): void => {
    console.error(message, error);

    // Check if this is a ResponseError with a status code
    const responseError = error as ResponseError;
    const status = responseError.status || responseError.response?.status;

    // Handle different HTTP status codes appropriately
    if (status) {
      // 401 Unauthorized - check refresh token before logging out
      if (status === 401) {
        console.log('[401 Handler] 401 Unauthorized - checking refresh token validity');

        // ROBUST MODE: Don't logout immediately - check if refresh token is still valid
        // This handles: offline, server errors, timeouts, bad network, etc.
        (async () => {
          const refreshToken = await getRefreshToken();
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            console.log('[401 Handler] Refresh token still valid - not logging out');
            console.log('[401 Handler] Possible temporary issue (offline/server error/timeout)');
            // Don't logout - user can continue with cached data
            return;
          } else {
            console.log('[401 Handler] Refresh token expired - logging out');
            alert(t('actions.sessionExpired'));
            window.location.href = '/signin';
          }
        })();
        return; // Don't send to monitoring
      }

      // 403 Forbidden - user doesn't have access
      if (status === 403) {
        console.log('403 Forbidden - access denied');
        alert(t('actions.noAccessToResource'));
        return; // Don't send to monitoring
      }

      // 404 Not Found - resource doesn't exist
      if (status === 404) {
        console.log('404 Not Found - resource not found');
        alert(t('actions.resourceNoLongerExists'));
        return; // Don't send to monitoring
      }

      // Other 4xx errors (400, 422, 409, etc.) - may indicate bugs, send to monitoring
      if (status >= 400 && status < 500) {
        console.error(`${status} Client Error - potential bug, sending to monitoring`);
        // Attach status to error object so monitoring noise filter (shouldReportError) can access it
        if (responseError && !responseError.status) {
          responseError.status = status;
        }
        notifyError(error);
        alert(message);
        return;
      }

      // 500-599 (server errors) - send to monitoring
      if (status >= 500) {
        console.error(`${status} Server Error - sending to monitoring`);
        // Attach status to error object so monitoring noise filter (shouldReportError) can access it
        if (responseError && !responseError.status) {
          responseError.status = status;
        }
        notifyError(error);
        alert(t('actions.unexpectedServerError'));
        return;
      }
    }

    // For errors without status code, send to monitoring
    notifyError(error);
    alert(message);
  };
}

async function fetchOrganization(api: ApiClient, id: number): Promise<CompleteOrganizationViewModel> {
  return await api.organization.fetchOrganizationWithEntities({
    organizationId: id.toString(),
  });
}

async function fetchOrganizationEventOccurrences(api: ApiClient, id: number): Promise<Array<EventOccurrenceViewModel>> {
  return await api.eventOccurrences.listEventOccurrences({
    organizationId: id.toString(),
  });
}

async function fetchOrganizationMeetingOccurrences(
  api: ApiClient,
  id: number,
): Promise<Array<MeetingOccurrenceViewModel>> {
  return await api.meetingOccurrences.listMeetingOccurrences({
    organizationId: id.toString(),
  });
}
async function fetchOrganizationTopics(api: ApiClient, id: number): Promise<Array<TopicViewModel>> {
  return await api.topics.listTopics({
    organizationId: id.toString(),
  });
}

async function fetchEntity(api: ApiClient, path: string): Promise<any> {
  const p = path.split('/');
  console.log(p);

  const response = await fetch(getBasePath() + path, {
    headers: await withAuthorization(api, {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': __APP_VERSION__,
    }),
  });
  if (response.status >= 200 && response.status < 300) {
    return response.json();
  } else if (response.status === 404) {
    return undefined;
  } else {
    // Provide descriptive error message with path and status code
    const statusText = response.statusText && response.statusText.trim() !== '' ? response.statusText : 'HTTP error';
    const errorMessage = `${statusText} (status: ${response.status}, path: ${path})`;
    const error = new ResponseError(errorMessage);
    error.response = response;
    error.status = response.status;
    throw error;
  }
}

export async function fetchPageGroups(api: ApiClient, id: number): Promise<PageGroupViewModel[]> {
  return await api.organization.listPageGroups({
    organizationId: '' + id,
  });
}

async function fetchReferences(api: ApiClient, id: number): Promise<Array<ReferenceViewModel>> {
  return await api.organization.listReferences({
    organizationId: '' + id,
  });
}

export const loadedOrganization: ActionCreator<LoadedOrganizationAction> = (
  id: number,
  organization: CompleteOrganizationViewModel,
  user: User,
) => {
  const username = user.username;
  const key = user.key;

  return {
    type: 'LOADED_ORGANIZATION',
    id: id,
    organization: organization,
    username: username,
    key: key,
    user: user,
  };
};

export const loadedCachedOrganization: ActionCreator<LoadedCachedOrganizationAction> = (
  id: number,
  organization: OrganizationState,
  user: {
    key: string;
    username: string;
    token: string;
  },
) => {
  const token = user.token;
  const username = user.username;
  const key = user.key;

  return {
    type: 'LOADED_CACHED_ORGANIZATION',
    id: id,
    organization: organization,
    token: token,
    username: username,
    key: key,
    user: user,
  };
};

export const loadedError: ActionCreator<LoadedErrorAction> = (error: Error | Record<string, unknown> | string) => {
  console.error(error);
  let errorMessage: string;
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (error && typeof error === 'object' && 'message' in error) {
    errorMessage = String(error.message);
  } else {
    errorMessage = String(error);
  }
  return {
    type: 'LOADED_ERROR',
    error: errorMessage,
  };
};

export const loadingOrganization: ActionCreator<LoadingOrganizationAction> = () => {
  return {
    type: 'LOADING_ORGANIZATION',
  };
};

export const loadedPageGroups: ActionCreator<LoadedPageGroupsAction> = (pages: PageGroupViewModel[]) => {
  return {
    type: 'LOADED_PAGE_GROUPS',
    pageGroups: pages,
  };
};

export const loadedReferences: ActionCreator<LoadedReferencesAction> = (references: ReferenceViewModel[]) => {
  return {
    type: 'LOADED_REFERENCES',
    references: references,
  };
};

export const loadedEventOccurrences: ActionCreator<LoadedEventOccurrencesAction> = (
  eventOccurrences: EventOccurrenceViewModel[],
) => {
  return {
    type: 'LOADED_EVENT_OCCURRENCES',
    eventOccurrences: eventOccurrences,
  };
};

export const loadedMeetingOccurrences: ActionCreator<LoadedMeetingOccurrencesAction> = (
  meetingOccurrences: MeetingOccurrenceViewModel[],
) => {
  return {
    type: 'LOADED_MEETING_OCCURRENCES',
    meetingOccurrences: meetingOccurrences,
  };
};

export const loadedTopics: ActionCreator<LoadedTopicsAction> = (topics: TopicViewModel[]) => {
  return {
    type: 'LOADED_TOPICS',
    topics: topics,
  };
};

export const loadedTutorials: ActionCreator<LoadedTutorialsAction> = (tutorials: TutorialViewModel[]) => {
  return {
    type: 'LOADED_TUTORIALS',
    tutorials: tutorials,
  };
};

export const loadedCompetence: ActionCreator<LoadedCompetenceAction> = (competence: CompetenceViewModel[]) => {
  return {
    type: 'LOADED_COMPETENCE',
    competence: competence,
  };
};

export const loadedScreens: ActionCreator<LoadedScreensAction> = (screens: Screen[]) => {
  return {
    type: 'LOADED_SCREENS',
    screens: screens,
  };
};

export function reloadScreens(organizationId: number): ThunkResult<Promise<void>> {
  return async (dispatch) => {
    try {
      const screenList = await getScreensForOrganization(organizationId);

      const enrichedScreens = await Promise.all(
        screenList.map(async (screen) => {
          try {
            const details = await fetchScreen(screen.displayId, organizationId);
            console.log(`[reloadScreens] Screen ${screen.displayId} details:`, JSON.stringify(details, null, 2));
            return {
              displayId: screen.displayId,
              name: details.name || screen.name,
              playerConnected: screen.playerConnected,
              orderStatus: screen.orderStatus,
              screenshotUrl: details.screenshotUrl || screen.screenshotUrl,
              workWeek: details.workWeek,
              previewUrl: details.previewUrl,
            };
          } catch (error) {
            console.error(`[reloadScreens] Failed to fetch details for screen ${screen.displayId}:`, error);
            return screen;
          }
        }),
      );

      dispatch(loadedScreens(enrichedScreens));
    } catch (error) {
      console.error('[reloadScreens] Failed to fetch screens:', error);
    }
  };
}

/**
 * TEMPORARY OVERRIDE of the server-generated page structure (pageGroups).
 *
 * The main menu is built verbatim from the backend mal/template (pageGroups), so a
 * page only shows up if the server includes it. Some account types are missing pages
 * there, so we inject them client-side until the server's mal is updated:
 *  - PERFORMANCE_REVIEW accounts (and org 2952): the Møter/Medarbeidersamtaler page in
 *    the "current" group, plus the Personale page in the "organization" group.
 *  - SIGNAGE accounts: the Personale page in the "organization" group, so a standalone
 *    infoskjerm order gets the staff menu like other single modules (e.g. Avvik/ISSUES).
 *
 * TODO: Remove this override once the server properly includes these pages.
 */
export function applyTemporaryPageGroupOverrides(
  pageGroups: PageGroupViewModel[],
  organization: OrganizationState | undefined,
  id: number,
): PageGroupViewModel[] {
  const hasPerformanceReview = organization?.features?.includes('PERFORMANCE_REVIEW') ?? false;
  const hasSignage = organization?.features?.includes('SIGNAGE') ?? false;
  const isOrg2952 = id === 2952;

  // The Møter page is only injected for PERFORMANCE_REVIEW accounts (and org 2952).
  const needsMeetingsPage = hasPerformanceReview || isOrg2952;
  // The Personale page is additionally injected for standalone SIGNAGE orders.
  const needsPersonalePage = needsMeetingsPage || hasSignage;

  if (!needsPersonalePage) {
    return pageGroups;
  }

  return pageGroups.map((group) => {
    if (needsMeetingsPage && group.code === 'current') {
      // Check if Møter page already exists and update its name or add it
      const hasMeetings = organization?.features?.includes('MEETINGS');
      const pageName =
        hasPerformanceReview && !hasMeetings ? t('actions.employeeInterviewsPageName') : t('actions.meetingsPageName');
      const meetingsPageIndex = group.pages.findIndex((page) => page.pageId === pageIds.meetings);

      if (meetingsPageIndex !== -1) {
        // Update existing page name
        const updatedPages = [...group.pages];
        updatedPages[meetingsPageIndex] = { ...updatedPages[meetingsPageIndex], name: pageName };
        return { ...group, pages: updatedPages };
      } else {
        // Add new page after Overblikk
        const updatedPages = [...group.pages];
        const overblikkIndex = updatedPages.findIndex((page) => page.pageId === pageIds.status);
        if (overblikkIndex !== -1) {
          updatedPages.splice(overblikkIndex + 1, 0, {
            pageId: pageIds.meetings,
            name: pageName,
            helpContent: '',
          });
        } else {
          // If Overblikk not found, add at the end
          updatedPages.push({
            pageId: pageIds.meetings,
            name: pageName,
            helpContent: '',
          });
        }
        return { ...group, pages: updatedPages };
      }
    } else if (group.code === 'organization') {
      // Check if Personale page already exists
      const hasPersonalePage = group.pages.some((page) => page.pageId === pageIds.employees);
      if (!hasPersonalePage) {
        // Add Personale page to organization group
        const updatedPages = [...group.pages];
        updatedPages.push({
          pageId: pageIds.employees,
          name: t('actions.staffPageName'),
          helpContent: '',
        });
        return { ...group, pages: updatedPages };
      }
    }
    return group;
  });
}

export function loadOrganization(
  id: number,
  user: {
    username: string;
    organizations: OrganizationReference[];
  },
  api: ApiClient,
): ThunkResult<Promise<void>> {
  // Invert control!
  // Return a function that accepts `dispatch` so we can dispatch later.
  // Thunk middleware knows how to turn thunk async actions into actions.

  return async (dispatch, getState) => {
    const timestamp = Date.now();

    // Set attachment cache context early so all paths use correct org cache
    import('src/services/attachment-cache').then(({ attachmentCache }) => {
      attachmentCache.setCurrentContext(user.username, id).catch((error) => {
        console.error('[AttachmentCache] Failed to set context:', error);
      });
    });

    const state = getState();
    const userOrganization = state.user?.organizations.find((o) => o.id === id);
    if (userOrganization === undefined) {
      console.error(`[loadOrganization ${timestamp}] ERROR: No user organization found`);
      const error = new Error('Illegal state (E701), no user organization');
      dispatch(loadedError(error));
    } else if (userOrganization.pending) {
      // Pending membership — nothing to load yet.
    } else if (userOrganization.requiresSecureLogin && state.user?.provider === '') {
      // Requires secure login before data can be loaded.
    } else if (state.loading) {
      // A load is already in flight — skip duplicate.
    } else if (state.loaded && state.organization?.organizationId === id) {
      // We already have this org loaded — refresh in background without flashing a loading state.
      // The org-id check is critical: when switching to a different org, we must take the loading
      // path below so the spinner shows instead of letting the user stare at the previous org's data.
      return dispatch(refreshOrganizationInBackground(id, user, api));
    } else {
      dispatch(loadingOrganization());

      // Time-to-usable ("org ready") for RUM (#13421): measured from the moment
      // we commit to loading until org data is first usable — from the local
      // cache or, failing that, from the network. One event per org load.
      const orgReadyStart = performance.now();
      let orgReadyRecorded = false;
      const recordOrgReady = (source: 'cache' | 'network', cold: boolean) => {
        if (orgReadyRecorded) return;
        orgReadyRecorded = true;
        recordEvent('dabih_org_ready', {
          organizationId: id,
          ms: Math.round(performance.now() - orgReadyStart),
          source,
          cold,
        });
      };

      // Add timeout protection - if loading takes more than 90 seconds, dispatch error
      const timeoutId = setTimeout(() => {
        console.error(`[loadOrganization ${timestamp}] TIMEOUT: Loading took more than 90 seconds`);
        dispatch(loadedError({ message: 'Loading timeout - organization data took too long to load' }));
      }, 90000);

      const p = await cache.get(user.username + '-page-groups-' + id);
      if (p !== null) {
        dispatch(loadedPageGroups(p));
      }

      const r = await cache.get(user.username + '-references-' + id);
      if (r !== null) {
        dispatch(loadedReferences(r));
      }

      const key = user.username + '-organization-' + id;
      const o = await cache.get(key);
      if (o !== null) {
        dispatch(loadedCachedOrganization(id, o, user));
        recordOrgReady('cache', false);
      }

      let pageGroups;
      try {
        pageGroups = await fetchPageGroups(api, id);
      } catch (error) {
        console.error(`[loadOrganization ${timestamp}] ERROR fetching page groups:`, error);
        clearTimeout(timeoutId);
        dispatch(loadedError({ message: 'Failed to load page groups', error }));
        return;
      }

      // TEMPORARY OVERRIDE: inject pages the server-generated mal is missing for certain
      // account types (PERFORMANCE_REVIEW, SIGNAGE). A fresh getState() is used because
      // loadedCachedOrganization above may have populated state.organization.
      // TODO: Remove once the server properly includes these pages.
      pageGroups = applyTemporaryPageGroupOverrides(pageGroups, getState().organization, id);

      dispatch(loadedPageGroups(pageGroups));

      let references;
      try {
        references = await fetchReferences(api, id);
        dispatch(loadedReferences(references));
      } catch (error) {
        console.error(`[loadOrganization ${timestamp}] ERROR fetching references:`, error);
        clearTimeout(timeoutId);
        dispatch(loadedError({ message: 'Failed to load references', error }));
        return;
      }

      fetchOrganization(api, id).then(
        (organization) => {
          clearTimeout(timeoutId); // Clear timeout on success
          dispatch(loadedOrganization(id, organization, user));
          recordOrgReady('network', true);

          // Start background downloader (context already set at top of loadOrganization)
          import('src/services/attachment-downloader').then(({ attachmentDownloader }) => {
            attachmentDownloader.start();
          });

          fetchOrganizationEventOccurrences(api, id).then((eventOccurrences) => {
            dispatch(loadedEventOccurrences(eventOccurrences));
          });

          fetchOrganizationMeetingOccurrences(api, id).then((meetingOccurrences) => {
            dispatch(loadedMeetingOccurrences(meetingOccurrences));
          });

          fetchOrganizationTopics(api, id).then((topics) => {
            dispatch(loadedTopics(topics));
          });

          fetchCompetence(api, id)
            .then((competence) => {
              dispatch(loadedCompetence(competence));
            })
            .catch((error) => {
              console.error('Failed to load competence:', error);
            });

          fetchTutorials(api, id).then(async (tutorials) => {
            dispatch(loadedTutorials(tutorials));

            // Preload all tutorial content versions
            const state = getState();
            const uniqueVersions = new Map<string, { tutorialId: number; contentVersionId: string }>();

            // Collect all unique contentVersionId across all tutorials
            tutorials.forEach((tutorial) => {
              if (tutorial.contentVersionId) {
                const cacheKey = `${tutorial.id}-${tutorial.contentVersionId}`;
                // Only add if not already in cache
                if (!state.tutorialContentCache[cacheKey]) {
                  uniqueVersions.set(cacheKey, {
                    tutorialId: parseInt(tutorial.id, 10),
                    contentVersionId: tutorial.contentVersionId,
                  });
                }
              }
            });

            if (uniqueVersions.size > 0) {
              // Load all content versions in parallel
              const preloadPromises = Array.from(uniqueVersions.values()).map(
                async ({ tutorialId, contentVersionId }) => {
                  try {
                    const cacheKey = `${tutorialId}-${contentVersionId}`;
                    const content = await fetchTutorialContent(api, id, tutorialId, contentVersionId);
                    dispatch(cacheTutorialContent(cacheKey, content));
                  } catch (error) {
                    console.error(`[loadOrganization] Failed to preload content for tutorial ${tutorialId}:`, error);
                    // Don't throw - we don't want to break the flow if one content fails to load
                  }
                },
              );

              await Promise.all(preloadPromises);
            }
          });

          // Always fetch screens. We don't gate this on the page structure
          // (pageGroups), since that is generated from industry/activity and can
          // change. getScreensForOrganization returns an empty list when the
          // organization has no screens, so an extra call is harmless. Visibility
          // of the infoskjerm page in the menu is still driven by pageGroups.
          dispatch(reloadScreens(id));
        },
        (error) => {
          console.error(`[loadOrganization ${timestamp}] ERROR fetching organization:`, error);
          clearTimeout(timeoutId); // Clear timeout on error
          return dispatch(loadedError(error));
        },
      );
    }
  };
}

/**
 * Refresh organization data in the background without showing loading state
 * Used when network comes back online to update data without jarring UI flash
 */
export function refreshOrganizationInBackground(
  id: number,
  user: {
    username: string;
    organizations: OrganizationReference[];
  },
  api: ApiClient,
): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const timestamp = Date.now();
    const state = getState();
    const userOrganization = state.user?.organizations.find((o) => o.id === id);

    if (userOrganization === undefined) {
      console.error(`[refreshOrganizationInBackground ${timestamp}] ERROR: No user organization found`);
      return;
    }

    if (userOrganization.pending) {
      return;
    }

    if (userOrganization.requiresSecureLogin && state.user?.provider === '') {
      return;
    }

    // NOTE: We intentionally do NOT dispatch loadingOrganization() here
    // This allows data to refresh without showing loading spinner

    try {
      // Fetch page groups from API
      const pageGroups = await fetchPageGroups(api, id);

      // Apply the same temporary page-group overrides as loadOrganization. A fresh
      // getState() is used (rather than the snapshot captured at thunk entry) so a
      // concurrent update during the await above can't leave us acting on stale state.
      const processedPageGroups = applyTemporaryPageGroupOverrides(pageGroups, getState().organization, id);

      dispatch(loadedPageGroups(processedPageGroups));

      // Fetch references from API
      const references = await fetchReferences(api, id);
      dispatch(loadedReferences(references));

      // Fetch organization data from API
      const organization = await fetchOrganization(api, id);
      dispatch(loadedOrganization(id, organization, user));

      // Fetch additional data in parallel
      const [eventOccurrences, meetingOccurrences, topics] = await Promise.all([
        fetchOrganizationEventOccurrences(api, id),
        fetchOrganizationMeetingOccurrences(api, id),
        fetchOrganizationTopics(api, id),
      ]);

      dispatch(loadedEventOccurrences(eventOccurrences));
      dispatch(loadedMeetingOccurrences(meetingOccurrences));
      dispatch(loadedTopics(topics));

      // Always fetch screens. We don't gate this on the page structure
      // (pageGroups), since that is generated from industry/activity and can
      // change. getScreensForOrganization returns an empty list when the
      // organization has no screens, so an extra call is harmless. Visibility
      // of the infoskjerm page in the menu is still driven by pageGroups.
      await dispatch(reloadScreens(id));
    } catch (error) {
      console.error(`[refreshOrganizationInBackground ${timestamp}] ERROR:`, error);
      // Silently fail - we don't want to show error when refreshing in background
      // The user can still use cached data
    }
  };
}

interface MessageType {
  path: string;
  eventType: string;
  item?: unknown;
  hidden?: boolean;
}

export function updateItem(api: ApiClient, message: MessageType): ThunkResult<Promise<UpdateItemAction | void>> {
  // Invert control!
  // Return a function that accepts `dispatch` so we can dispatch later.
  // Thunk middleware knows how to turn thunk async actions into actions.
  return (dispatch): Promise<UpdateItemAction | void> => {
    // Special handling if organization is updated:
    if (message.path.endsWith('/archive')) {
      console.log(message.path);
    }

    if (message.path.endsWith('/archive')) {
      return Promise.resolve();
    } else if (message.eventType === 'delete') {
      dispatch(deleteItemAction(message));
    } else if (message.hidden) {
      dispatch(updateItemAction(message));
    } else if (message.item) {
      dispatch(updateItemAction(message));
      setTimeout(() => {
        fetchEntity(api, message.path).then(function (entity) {
          const m = Object.assign({}, message, { item: entity });
          dispatch(updateItemAction(m));
        });
      }, 600);
    } else {
      return fetchEntity(api, message.path).then(function (entity) {
        const m = Object.assign({}, message, { item: entity });

        return dispatch(updateItemAction(m));
      });
    }
    return Promise.resolve();
  };
}

// Export for batch notification handler
export { fetchEntity, fetchOrganizationMeetingOccurrences, fetchOrganizationEventOccurrences, withAuthorization };

export const taskDone =
  (api: ApiClient, taskUuid: string): ThunkResult<Promise<Response | void>> =>
  async (_dispatch, getState): Promise<Response | void> => {
    const state = getState();
    const id = state.id;
    if (id !== null) {
      const org = state.organization;

      if (org === undefined) {
        throw new Error('Illegal state (E702), organization not found');
      }
      const functionUuid = org.tasksById[taskUuid].functionUuid;
      return await api.functions.markTaskDone({
        organizationId: id.toString(),
        functionId: functionUuid,
        taskId: taskUuid,
      });
    }
  };

async function withAuthorization(api: ApiClient, headers: Record<string, string>): Promise<Record<string, string>> {
  const config = api.config;
  if (config.username !== undefined || config.password !== undefined) {
    headers['Authorization'] = 'Basic ' + Buffer.from(config.username + ':' + config.password).toString('base64');
  }
  if (config.accessToken) {
    const token = config.accessToken;
    const tokenString = await token('UserSecurityWithToken', []);

    if (tokenString) {
      headers['Authorization'] = `Bearer ${tokenString}`;
    }
  }
  return headers;
}

export const declineTaskTemplateUpdate =
  (api: ApiClient, taskUuid: string): ThunkResult<void> =>
  async (_dispatch, getState): Promise<Response | void> => {
    const state = getState();
    const id = state.id;
    const organization = getOrganization(state);

    if (organization === undefined) {
      throw new Error('Illegal state (E409), expected organization');
    }
    const task = organization.tasksById[taskUuid];
    const functionUuid = task.functionUuid;

    // return await api.functions.
    return fetch(
      BASE_PATH + '/organizations/' + id + '/functions/' + functionUuid + '/tasks/' + taskUuid + '/declineTemplate',
      {
        headers: await withAuthorization(api, {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': __APP_VERSION__,
        }),
        method: 'POST',
        body: '',
      },
    ).catch(onFetchRejected(saveErrorMessage()));
  };

export const declineTemplateUpdate =
  (api: ApiClient, entityType: string, entityUuid: string): ThunkResult<void> =>
  async (_dispatch, getState): Promise<Response | void> => {
    if (entityType === 'tasks') {
      // Ignore tasks. Decline using specific function 'declineTaskTemplateUpdate' only
      return Promise.resolve();
    }

    const state = getState();
    const id = state.id;

    return fetch(BASE_PATH + '/organizations/' + id + '/' + entityType + '/' + entityUuid + '/declineTemplate', {
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      method: 'POST',
      body: '',
    }).catch(onFetchRejected(saveErrorMessage()));
  };

export function urlForItem(entityType: string, item: { uuid: string }): string {
  const mapPrefix: {
    tasks: (i: any) => string;
    contacts: (i: any) => string;
    [key: string]: (i: any) => string;
  } = {
    tasks: function (i: any) {
      return '/functions/' + i.functionUuid;
    },
    contacts: function (i: any) {
      return '/partners/' + i.partnerUuid;
    },
  };

  const defaultPrefix = (): string => '';

  const f = mapPrefix[entityType] || defaultPrefix;

  return f(item) + '/' + entityType + '/' + item.uuid;
}

export const saveItem =
  (api: ApiClient, type: string, id: string, item: any, parentId = ''): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    console.log('saveItem', type, id, item, parentId);

    const state = getState();
    const organizationId = state.id;

    const organization = state.organization;
    if (organization === undefined) {
      throw new Error('Illegal state (E703), organization not found');
    }
    let originalItem = organization[type + 'ById'][id];
    let url: string;
    if (originalItem) {
      url = urlForItem(type, originalItem);
    } else if (type === 'tasks') {
      url = '/functions/' + item.functionUuid + '/tasks/' + id;
    } else {
      url = '/' + type + '/' + id;

      const m: {
        tasks: { [p: string]: any };
        contacts: { [p: string]: any };
        [key: string]: { [p: string]: any };
      } = {
        tasks: organization.functionsById,
        contacts: organization.partnersById,
      };

      const parentItem = m[type] && m[type][parentId];

      if (parentItem) {
        const p: {
          tasks: string;
          contacts: string;
          [key: string]: string;
        } = {
          tasks: 'functions',
          contacts: 'partners',
        };

        url = urlForItem(p[type], parentItem) + url;

        const defaults: {
          tasks: { function: string };
          contacts: Record<string, unknown>;
          [key: string]: Record<string, unknown>;
        } = {
          tasks: {
            function: item.functionUuid,
          },
          contacts: {},
        };

        originalItem = defaults[type];
      }
    }

    const updatedItem = Object.assign({}, originalItem, item, {
      uuid: id,
      saving: true,
    });

    const message = {
      path: '/organizations/' + organizationId + url,
      eventType: 'update',
      item: updatedItem,
    };

    dispatch({
      type: 'UPDATE_ITEM',
      message: message,
    });

    if (type === 'computers' && item.unitType === 'printer') {
      item = {
        unitType: item.unitType,
        type: 'UNDEFINED',
        name: item.name,
        networkUuid: item.networkUuid,
        serviceProvider: '',
        contracts: [],
        systemUpdateOperatorType: 'UNDEFINED',
        systemUpdateOperator: '',
        antiVirusOperatorType: 'UNDEFINED',
        antiVirusOperator: '',
        antiVirusRequirements: [],
        connectionType: 'OTHER',
        location: item.location,
        elevated: false,
        locked: false,
        restrictedPhysicalAccess: false,
        displayPositionSecure: false,
        mobileMedicalDataRequirements: [],
        notes: item.notes,
        printerPositionSecure: item.printerPositionSecure,
        isConfirmedEntity: item.isConfirmedEntity,
      };
    }

    return fetch(BASE_PATH + '/organizations/' + organizationId + url, {
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      method: 'PUT',
      body: JSON.stringify(item),
    })
      .then(function (res) {
        console.log(res);
        setTimeout(function () {
          const s = getState();
          const organization1 = s.organization;
          if (organization1 === undefined) {
            return;
          }
          const o = organization1[type + 'ById'][id];
          if (o) {
            console.log('After saving ' + type + ' ' + id + ': ' + o.saving);
            if (o.saving) {
              // Trigger GET item if message not received
              dispatch(
                updateItem(api, {
                  path: '/organizations/' + organizationId + url,
                  eventType: 'update',
                }),
              );
            }
          }
        }, 500);
      })
      .catch(onFetchRejected(saveErrorMessage()));
  };

export const createRiskAssessment = (
  api: ApiClient,
  uuid: string,
  name: string,
  organizationId: string,
): Promise<void> => {
  const p: CreateOrUpdateRiskAssessmentRequest = {
    organizationId: organizationId,
    riskAssessmentId: uuid,
    riskAssessmentUpdateMessage: {
      description: '',
      responsibleBy: '',
      probability: 0,
      consequence: 0,
      riskDescription: '',
      measures: '',
      assessmentDate: '',
      violatesInformationSecurityRequirements: false,
      violatesIntegrityRequirement: false,
      violatesConfidentialityRequirement: false,
      violatesNonRepudiationRequirement: false,
      violatesAvailabilityRequirement: false,
      determinedBy: '',
      name: name,
      pages: [pageIds.riskAssessments, pageIds.informationSecurity],
      isConfirmedEntity: true,
    },
  };
  return api.riskAssessments.createOrUpdateRiskAssessment(p);
};

/**
 * Set-replace av relaterte hendelser/oppgaver på en risikovurdering
 * (trinnvis/dabih-api#4573).
 */
export const setRelatedEventsForRiskAssessment = (
  api: ApiClient,
  organizationId: string,
  riskAssessmentId: string,
  eventUuids: string[],
): Promise<void> =>
  api.riskAssessments.setRelatedEventsForRiskAssessment({
    organizationId,
    riskAssessmentId,
    requestBody: eventUuids,
  });

export const setRelatedTasksForRiskAssessment = (
  api: ApiClient,
  organizationId: string,
  riskAssessmentId: string,
  taskUuids: string[],
): Promise<void> =>
  api.riskAssessments.setRelatedTasksForRiskAssessment({
    organizationId,
    riskAssessmentId,
    requestBody: taskUuids,
  });

/**
 * Publiser/avpubliser tiltakskort for en risikovurdering (trinnvis/dabih-api#4577).
 */
export const setMeasuresCardPublished = (
  api: ApiClient,
  organizationId: string,
  riskAssessmentId: string,
  published: boolean,
): Promise<void> =>
  published
    ? api.riskAssessments.publishMeasuresCardForRiskAssessment({ organizationId, riskAssessmentId })
    : api.riskAssessments.unpublishMeasuresCardForRiskAssessment({ organizationId, riskAssessmentId });

export const createContract = (api: ApiClient, uuid: string, name: string, organizationId: string): Promise<void> => {
  const p: CreateOrUpdateContractRequest = {
    organizationId: organizationId,
    contractId: uuid,
    contractUpdateMessage: {
      status: '',
      profession: '',
      sideCode: '',
      validFromDate: '',
      templateId: '',
      content: '',
      templateEmployeeId: '',
      category: '',
      partners: [],
      name: name,
      validToDate: '',
      documentLocation: '',
      affiliation: '',
      required: false,
      employees: [],
      accessControl: [],
      classification: 'NONE',
      pages: [pageIds.informationSecurity],
      isConfirmedEntity: true,
    },
  };

  return api.contracts.createOrUpdateContract(p);
};

export const createPartner = (api: ApiClient, uuid: string, name: string, organizationId: string): Promise<void> => {
  const p: CreateOrUpdatePartnerRequest = {
    organizationId: organizationId,
    partnerId: uuid,
    partnerUpdateMessage: {
      remoteAccess: false,
      phone: '',
      address: '',
      name: name,
      physicalAccess: false,
      pageAccess: false,
      industry: '',
      notes: '',
      pages: [pageIds.informationSecurity],
      email: '',
      url: '',
      isConfirmedEntity: true,
    },
  };
  return api.partners.createOrUpdatePartner(p);
};

export const createAsset = (
  api: ApiClient,
  uuid: string,
  name: string,
  number: string,
  organizationId: string,
): Promise<void> => {
  const p: CreateOrUpdateAssetRequest = {
    organizationId: organizationId,
    assetId: uuid,
    assetsUpdateMessage: {
      model: '',
      location: '',
      maintenanceRequired: false,
      number: number,
      competency: '',
      userManual: '',
      storesSensitiveInformation: false,
      accessory: '',
      manufacturer: '',
      name: name,
      supplier: '',
      assetVerified: false,
      discardedYear: '',
      documentationVerified: false,
      requiresElectricity: false,
      acquiredYear: '',
      owner: '',
      maintenanceInterval: '',
      documented: false,
      category: 'Ingen',
      maintenance: '',
      complianceDeclaration: '',
      isConfirmedEntity: true,
      radiationType: '',
      radiation: false,
      dsaApproved: false,
      dsaRegistered: false,
    },
  };

  return api.assets.createOrUpdateAsset(p);
};

/**
 * Gets the first name, technically gets all words leading up to the last
 * Example: "Blake Robertson" --> "Blake"
 * Example: "Blake Andrew Robertson" --> "Blake Andrew"
 * Example: "Blake" --> "Blake"
 * @param str
 * @returns {*}
 */
const getFirstName = function (str: string): string {
  const arr = str.split(' ');
  if (arr.length === 1) {
    return arr[0];
  }
  return arr.slice(0, -1).join(' '); // returns "Paul Steve"
};

/**
 * Gets the last name (e.g. the last word in the supplied string)
 * Example: "Blake Robertson" --> "Robertson"
 * Example: "Blake Andrew Robertson" --> "Robertson"
 * Example: "Blake" --> "<None>"
 * @param str
 * @param {string} [ifNone] optional default value if there is not last name, defaults to "<None>"
 * @returns {string}
 */
const getLastName = function (str: string, ifNone: string): string {
  const arr = str.split(' ');
  if (arr.length === 1) {
    return ifNone || '<None>';
  }
  return arr.slice(-1).join(' ');
};

export const createEmployee = (api: ApiClient, uuid: string, name: string, organizationId: string): Promise<void> => {
  const p: CreateOrUpdateEmployeeRequest = {
    organizationId: organizationId,
    employeeId: uuid,
    employeeUpdateMessage: {
      lastName: getLastName(name, ''),
      phone: '',
      associationType: '',
      profession: '',
      accessLevel: 'NONE',
      secondaryPhone: '',
      hprNumber: '',
      address: '',
      email: '',
      herNumber: '',
      notes: '',
      firstName: getFirstName(name),
      expertise: '',
      status: 'ACTIVE',
      gender: EmployeeUpdateMessageGenderEnum.Undefined,
      isConfirmedEntity: true,
      nextOfKin: '',
    },
  };

  return api.employees.createOrUpdateEmployee(p);
};

export const deleteItem =
  (api: ApiClient, entityType: string, uuid: string): ThunkResult<void> =>
  async (dispatch, getState): Promise<Response | void> => {
    const state = getState();
    const id = state.id;
    if (id !== null) {
      const organization1 = state.organization;
      if (organization1 === undefined) {
        throw new Error('Illegal state (E704), organization not found');
      }
      const uuidNoInstance = uuid.split(':')[0];
      const item = organization1[entityType + 'ById'][uuidNoInstance];

      const newItem = { ...item, deleted: true };
      const url = urlForItem(entityType, item);

      dispatch({
        type: 'UPDATE_ITEM',
        message: {
          path: '/organizations/' + id + url,
          eventType: 'update',
          item: newItem,
        },
      });

      switch (entityType) {
        case 'assets':
          return await api.assets.deleteAsset({ organizationId: id.toString(), assetId: uuid });
        case 'constitutionalDocuments':
          return await api.constitutionalDocuments.deleteConstitutionalDocument({
            organizationId: id.toString(),
            constitutionalDocumentId: uuid,
          });
        case 'contacts':
          return await api.partners.deletePartnerContact({
            organizationId: id.toString(),
            partnerId: item.partnerUuid,
            contactId: uuid,
          });
        case 'contracts':
          return await api.contracts.deleteContract({ organizationId: id.toString(), contractId: uuid });
        case 'competence':
          return await api.competence.deleteCompetence({ organizationId: id.toString(), competenceId: uuid });
        case 'documents':
          return await api.documents.deleteDocument({ organizationId: id.toString(), documentId: uuid });
        case 'employees':
          return await api.employees.deleteEmployee({ organizationId: id.toString(), employeeId: uuid });
        case 'functions':
          return await api.functions.deleteFunction({ organizationId: id.toString(), functionId: uuid });
        case 'guidelines':
          return await api.guidelines.deleteGuideline({ organizationId: id.toString(), guidelineId: uuid });
        case 'issues':
          return await api.issues.deleteIssue({ organizationId: id.toString(), issueId: uuid });
        case 'partners':
          return await api.partners.deletePartner({ organizationId: id.toString(), partnerId: uuid });
        case 'reports':
          return await api.reports.deleteReport({ organizationId: id.toString(), reportId: uuid });
        case 'riskAssessments':
          return await api.riskAssessments.deleteRiskAssessment({
            organizationId: id.toString(),
            riskAssessmentId: uuid,
          });
        case 'substances':
          return await api.substances.deleteSubstance({ organizationId: id.toString(), substanceId: uuid });
        case 'tasks':
          return await api.functions.deleteFunctionTask({
            organizationId: id.toString(),
            functionId: item.functionUuid,
            taskId: uuid,
          });
        case 'computers':
          return await api.computers.deleteComputer({ organizationId: id.toString(), computerId: uuid });
        case 'networks':
          return await api.computers.deleteNetwork({ organizationId: id.toString(), networkId: uuid });
        case 'externalConnections':
          return await api.computers.deleteExternalConnection({
            organizationId: id.toString(),
            externalConnectionId: uuid,
          });
      }
    }
  };

function uploadEndpoint(uuid: string, organization1: OrganizationState, entityType: string, id: number, file: File) {
  const { url, endpoint } = attachmentEndpoint(uuid, organization1, entityType, id);
  return { url, endpoint: endpoint + '/' + encodeURIComponent(file.name) };
}

function attachmentEndpoint(uuid: string, organization1: OrganizationState, entityType: string, id: number) {
  if (entityType === 'eventOccurrences' || entityType === 'meetingOccurrences') {
    const url = '/' + entityType + '/' + uuid;

    const endpoint = BASE_PATH + '/organizations/' + id + url + '/attachments';
    return { url, endpoint };
  } else {
    const uuidNoInstance = uuid.split(':')[0];
    const item = organization1[entityType + 'ById'][uuidNoInstance];

    const url = urlForItem(entityType, item);

    const endpoint = BASE_PATH + '/organizations/' + id + url + '/attachments';
    return { url, endpoint };
  }
}

export const uploadFile =
  (api: ApiClient, entityType: string, uuid: string, file: File): ThunkResult<void> =>
  async (dispatch, getState): Promise<Response | void> => {
    const state = getState();
    const id = state.id;
    if (id === null) {
      throw new Error('Illegal state (E706), id not found');
    }

    const organization1 = state.organization;
    if (organization1 === undefined) {
      throw new Error('Illegal state (E705), organization not found');
    }
    const { url, endpoint } = uploadEndpoint(uuid, organization1, entityType, id, file);

    return fetch(endpoint, {
      method: 'PUT',
      headers: await withAuthorization(api, {
        'X-Requested-With': __APP_VERSION__,
        'Content-Type': 'application/octet-stream',
      }),
      body: file,
    })
      .then(checkStatus)
      .then(() => {
        dispatch(
          updateItem(api, {
            path: '/organizations/' + id + url,
            eventType: 'update',
          }),
        );
      })
      .catch(onFetchRejected(t('actions.uploadError')));
  };

export const renameAttachment =
  (api: ApiClient, entityType: string, uuid: string, attachmentId: string, name: string): ThunkResult<void> =>
  async (dispatch, getState): Promise<Response | void> => {
    const state = getState();
    const id = state.id;
    if (id === null) {
      throw new Error('Illegal state (E706), id not found');
    }

    const organization1 = state.organization;
    if (organization1 === undefined) {
      throw new Error('Illegal state (E706), organization not found');
    }

    const e = attachmentEndpoint(uuid, organization1, entityType, id);

    const endpoint = e.endpoint + '/' + attachmentId + '/properties';

    const p = {
      name: name,
    };
    return fetch(endpoint, {
      method: 'PUT',
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      body: JSON.stringify(p),
    })
      .then(checkStatus)
      .then(() => {
        dispatch(
          updateItem(api, {
            path: '/organizations/' + id + e.url,
            eventType: 'update',
          }),
        );
      })
      .catch(onFetchRejected(t('actions.attachmentChangeError')));
  };

export const deleteAttachment =
  (api: ApiClient, entityType: string, uuid: string, attachmentId: string): ThunkResult<void> =>
  async (dispatch, getState): Promise<Response | void> => {
    const state = getState();
    const id = state.id;
    if (id === null) {
      throw new Error('Illegal state (E706), id not found');
    }

    const organization1 = state.organization;
    if (organization1 === undefined) {
      throw new Error('Illegal state (E707), organization not found');
    }

    const e = attachmentEndpoint(uuid, organization1, entityType, id);

    const endpoint = e.endpoint + '/' + attachmentId;

    return fetch(endpoint, {
      method: 'DELETE',
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
    })
      .then(checkStatus)
      .then(() => {
        dispatch(
          updateItem(api, {
            path: '/organizations/' + id + e.url,
            eventType: 'update',
          }),
        );
      })
      .catch(onFetchRejected(t('actions.attachmentDeleteError')));
  };

export const updateOrganization =
  (api: ApiClient, item: any): ThunkResult<void> =>
  async (dispatch, getState): Promise<void> => {
    const state = getState();
    const id = state.id;

    fetch(BASE_PATH + '/organizations/' + id, {
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      method: 'PUT',
      body: JSON.stringify(item),
    })
      .then((res) => {
        console.log(res);
      })
      .then(() => {
        dispatch(
          updateItem(api, {
            path: '/organizations/' + id,
            eventType: 'update',
          }),
        );
      })
      .catch(onFetchRejected(saveErrorMessage()));
  };

/**
 * Updates the additional properties of the current user.
 *
 * User properties are used for properties that are relevant only to the current user. This includes per organization
 * properties. To set
 *
 * @param p Object with keys for each additional property to set. Other additional
 *        properties are not affected. Set value to null for a key to delete the key.
 * @returns {Function}
 */
export const updateUserProperties = (api: ApiClient, p: Record<string, unknown>): ThunkResult<Promise<void>> => {
  // Invert control!
  // Return a function that accepts `dispatch` so we can dispatch later.
  // Thunk middleware knows how to turn thunk async actions into actions.

  return async (dispatch, getState): Promise<void> => {
    const state = getState();

    if (!state.organization) {
      return new Promise<void>((resolve) => resolve());
    }

    dispatch(updateUserPropertiesAction(p));

    return fetch(BASE_PATH + '/accounts/properties', {
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      method: 'PUT',
      body: JSON.stringify(p),
    })
      .then(function (res) {
        console.log(res);
      })
      .catch(onFetchRejected(t('actions.cannotUpdate')));
  };
};

/**
 * Updates the additional properties of the current user.
 *
 * User properties are used for properties that are relevant only to the current user. This includes per organization
 * properties. To set
 *
 * @param p Object with keys for each additional property to set. Other additional
 *        properties are not affected. Set value to null for a key to delete the key.
 * @param organizationId
 * @returns {Function}
 */
export const updateUserPropertiesForOrganization = (
  api: ApiClient,
  p: { [key: string]: any },
  organizationId: number,
): ThunkResult<Promise<void>> => {
  const prefixKeys = (m: { [key: string]: any }, prefix: string): { [key: string]: any } => {
    const keys = Object.keys(m);
    const result: { [key: string]: any } = {};

    keys.forEach(function (k) {
      const key = prefix + k;
      result[key] = m[k];
    });

    return result;
  };

  const prefixedProperties = prefixKeys(p, organizationId + '_');

  return updateUserProperties(api, prefixedProperties);
};

/**
 * Send a message to employees of an organization.
 *
 * @param p The send message command object.
 * @returns {Function}
 */
export const sendMessage = (api: ApiClient, p: SendMessageCommand): ThunkResult<Promise<void>> => {
  return sendOrganizationMessage(api, JSON.stringify(p), ':send-message');
};

export const saveTask =
  (api: ApiClient, id: string, item: FunctionTaskUpdateMessage): ThunkResult<void> =>
  async (dispatch, getState): Promise<void> => {
    console.log('saveTask', id, item);

    const state = getState();
    const organizationId = state.id;
    const organization1 = state.organization;
    if (organization1 === undefined) {
      throw new Error('Illegal state (E708), organization not found');
    }

    const originalItem = organization1.tasksById[id];
    const url: string = '/functions/' + item.functionUuid + '/tasks/' + id;

    const updatedItem = Object.assign({}, originalItem, item, {
      uuid: id,
      saving: true,
    });

    const message = {
      path: '/organizations/' + organizationId + url,
      eventType: 'update',
      item: updatedItem,
    };

    dispatch({
      type: 'UPDATE_ITEM',
      message: message,
    });

    return fetch(BASE_PATH + '/organizations/' + organizationId + url, {
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      method: 'PUT',
      body: JSON.stringify(item),
    })
      .then(function (res) {
        console.log(res);
      })
      .catch(onFetchRejected(saveErrorMessage()));
  };

/**
 * Send a message to employees of an organization.
 *
 * @param p The send message command object.
 * @returns {Function}
 */
export const sendShareVacation = (api: ApiClient, p: SendShareVacationCommand): ThunkResult<Promise<void>> => {
  return sendOrganizationMessage(api, JSON.stringify(p), ':send-share-vacation');
};

function sendOrganizationMessage(api: ApiClient, body: string, target: string): ThunkResult<Promise<void>> {
  // Invert control!
  // Return a function that accepts `dispatch` so we can dispatch later.
  // Thunk middleware knows how to turn thunk async actions into actions.

  return async (_dispatch, getState): Promise<void> => {
    const state = getState();

    if (!state.organization) {
      return new Promise<void>((resolve) => resolve());
    }

    const id = state.id;

    return fetch(BASE_PATH + '/organizations/' + id + '/' + target, {
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      method: 'POST',
      body: body,
    })
      .then(function (res) {
        console.log(res);
      })
      .catch(onFetchRejected(t('actions.cannotSendMessage')));
  };
}

/**
 * Send a message to employees of an organization.
 *
 * @param p The send message command object.
 * @returns {Function}
 */
export const stopShareVacation = (api: ApiClient, p: StopShareVacationCommand): ThunkResult<Promise<void>> => {
  return sendOrganizationMessage(api, JSON.stringify(p), ':stop-share-vacation');
};

export const loginUserAction = (
  api: ApiClient,
  username: string,
): ThunkAction<Promise<boolean>, State, unknown, DAction> => {
  return async (dispatch): Promise<boolean> => {
    const timestamp = Date.now();
    console.log(`[loginUserAction ${timestamp}] Called with username:`, username);

    try {
      console.log(`[loginUserAction ${timestamp}] Calling api.accounts.listOrganizations...`);

      let response;
      try {
        response = await api.accounts.listOrganizations({ accountEmail: username });
      } catch (fetchError: any) {
        console.error(`[loginUserAction ${timestamp}] API fetch failed:`, {
          message: fetchError?.message,
          status: fetchError?.response?.status,
        });
        throw fetchError;
      }

      console.log(`[loginUserAction ${timestamp}] Response received`);
      const organizations = response.organizations;

      setMonitoringUser(undefined, username);

      try {
        setPersistedAuthIdentity({
          username,
          provider: response.provider,
          issuedAt: new Date().toISOString(),
        });
      } catch (e: any) {
        console.error(e);
        notifyError(e);
      }

      const user = {
        ...response,
        username: username,
        organizations: organizations,
        key: response.key,
        ablyTokenRequest: response.ablyTokenRequest,
      };

      dispatch(updateUserAction(user));

      return true;
    } catch (_e) {
      console.error(`[loginUserAction ${timestamp}] Failed, attempting cached user fallback`);

      // Check if refresh token is still valid before giving up
      const refreshTokenValue = await getRefreshToken();
      if (!refreshTokenValue || isRefreshTokenExpired(refreshTokenValue)) {
        console.log(`[loginUserAction ${timestamp}] Refresh token expired - cannot use cache`);
        return false;
      }

      // Try to load cached user data from IndexedDB
      try {
        const cachedUser = (await cache.get(username)) as User | null;
        if (cachedUser && cachedUser.username && cachedUser.organizations) {
          console.log(`[loginUserAction ${timestamp}] Using cached user data for:`, username);

          setMonitoringUser(undefined, username);
          setPersistedAuthIdentity({
            username,
            provider: cachedUser.provider,
          });
          dispatch(updateUserAction(cachedUser));

          // Background refresh: retry fetching fresh data without blocking the user
          setTimeout(async () => {
            try {
              console.log(`[loginUserAction bg-refresh] Refreshing user data in background...`);
              const response = await api.accounts.listOrganizations({ accountEmail: username });
              const freshUser = {
                ...response,
                username: username,
                organizations: response.organizations,
                key: response.key,
                ablyTokenRequest: response.ablyTokenRequest,
              };
              setPersistedAuthIdentity({
                username,
                provider: freshUser.provider,
              });
              dispatch(updateUserAction(freshUser));
              console.log(`[loginUserAction bg-refresh] User data refreshed successfully`);
            } catch (bgError) {
              console.warn(`[loginUserAction bg-refresh] Background refresh failed:`, bgError);
            }
          }, 5000);

          return true;
        }
      } catch (cacheError) {
        console.error(`[loginUserAction ${timestamp}] Cache read failed:`, cacheError);
      }

      console.log(`[loginUserAction ${timestamp}] No cached user data available`);
      return false;
    }
  };
};

export function updateDataItemAdditionalProperties(
  api: ApiClient,
  entityType: string,
  uuid: string,
  properties: Record<string, unknown>,
): ThunkResult<Promise<void>> {
  return async (dispatch, getState): Promise<void> => {
    const state = getState();

    if (!state.organization) {
      return Promise.resolve();
    }

    const id = state.id;
    const originalItem = state.organization[entityType + 'ById'][uuid];
    const updatedItem = Object.assign({}, originalItem, properties, {
      saving: true,
    });
    const message = {
      path: '/organizations/' + id + '/' + entityType + '/' + uuid,
      eventType: 'update',
      item: updatedItem,
    };

    dispatch({
      type: 'UPDATE_ITEM',
      message: message,
    });

    return fetch(BASE_PATH + '/organizations/' + id + '/' + entityType + '/' + uuid + '/properties', {
      headers: await withAuthorization(api, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': __APP_VERSION__,
      }),
      method: 'PUT',
      body: JSON.stringify(properties),
    })
      .then(function (res) {
        console.log(res);
        setTimeout(function () {
          const s = getState();
          const organization = s.organization;
          if (organization !== undefined) {
            const o = organization[entityType + 'ById'][uuid];
            if (o) {
              console.log('After saving properties ' + entityType + ': ' + uuid + ': ' + o.saving);
              if (o.saving) {
                // Trigger GET item if message not received
                dispatch(
                  updateItem(api, {
                    path: '/organizations/' + id + '/' + entityType + '/' + uuid,
                    eventType: 'update',
                  }),
                );
              }
            }
          }
        }, 500);
      })
      .catch(function (res) {
        console.log(res);
      });
  };
}

/**
 * Tutorial Registration Actions
 */

/**
 * Fetch all tutorials for an organization
 */
export async function fetchTutorials(api: ApiClient, organizationId: number): Promise<TutorialViewModel[]> {
  try {
    const tutorials = await api.tutorials.getTutorials({
      organizationId,
    });
    return tutorials;
  } catch (error) {
    console.error('Failed to fetch tutorials:', error);
    throw error;
  }
}

/**
 * Fetch all competence registrations for an organization.
 *
 * Competence is not part of the organization snapshot, so it is loaded
 * separately (like tutorials) and normalised into competenceById.
 */
export async function fetchCompetence(api: ApiClient, organizationId: number): Promise<CompetenceViewModel[]> {
  try {
    return await api.competence.listCompetence({
      organizationId: organizationId.toString(),
    });
  } catch (error) {
    console.error('Failed to fetch competence:', error);
    throw error;
  }
}

/**
 * Fetch a single tutorial by ID
 */
export async function fetchTutorial(
  api: ApiClient,
  organizationId: number,
  tutorialId: number,
): Promise<TutorialViewModel> {
  try {
    const tutorial = await api.tutorials.getTutorial({
      organizationId,
      tutorialId,
    });
    return tutorial;
  } catch (error) {
    console.error('Failed to fetch tutorial:', error);
    throw error;
  }
}

/**
 * Fetch tutorial content for a specific version
 * Content is immutable and can be cached aggressively (1 year)
 */
export async function fetchTutorialContent(
  api: ApiClient,
  organizationId: number,
  tutorialId: number,
  versionId: string,
): Promise<any> {
  try {
    const content = await api.tutorials.getTutorialContent({
      organizationId,
      tutorialId,
      versionId,
    });

    console.log('[fetchTutorialContent] Raw API response:', {
      tutorialId,
      versionId,
      content,
      partsCount: content.parts?.length,
      partsWithTests: content.parts?.filter((p: any) => p.test && p.test.length > 0).length || 0,
      firstPartTest: content.parts?.[0]?.test,
    });

    return content;
  } catch (error) {
    console.error('Failed to fetch tutorial content:', error);
    throw error;
  }
}

/**
 * Create a new tutorial registration
 */
export async function createTutorialRegistration(
  api: ApiClient,
  organizationId: number,
  tutorialId: number,
  request: {
    registrationId: string;
    participants: Array<{ id: string; employeeUuid: string | null }>;
    dueDate: string | null;
    contentVersionId: string;
    comment?: string | null;
  },
): Promise<TutorialRegistration> {
  try {
    const registration = await api.tutorials.createTutorialRegistration({
      organizationId,
      tutorialId,
      createRegistrationRequest: {
        registrationId: request.registrationId,
        contentVersionId: request.contentVersionId,
        dueDate: request.dueDate || undefined,
        comment: request.comment || undefined,
        participants: request.participants.map((p) => ({
          id: p.id,
          employeeUuid: p.employeeUuid ?? undefined, // Use ?? to preserve null for guides
        })),
      },
    });
    return registration;
  } catch (error) {
    console.error('Failed to create tutorial registration:', error);
    throw error;
  }
}

/**
 * Fetch participant state (currentPart, partStates, rating)
 * This returns the progress state for a specific participant
 */
export async function fetchParticipantState(
  api: ApiClient,
  organizationId: number,
  tutorialId: number,
  participantId: string,
): Promise<ParticipantState> {
  try {
    const state = await api.tutorials.getParticipantState({
      organizationId,
      tutorialId,
      participantId,
    });
    return state;
  } catch (error) {
    console.error('Failed to fetch participant state:', error);
    throw error;
  }
}

/**
 * Update participant state (progress, completion, etc.)
 */
export async function updateParticipantState(
  api: ApiClient,
  organizationId: number,
  tutorialId: number,
  participantId: string,
  request: {
    currentPart?: number;
    partStates?: string[];
    rating?: number;
    completed?: boolean;
  },
): Promise<any> {
  try {
    const participant = await api.tutorials.updateParticipantState({
      organizationId,
      tutorialId,
      participantId,
      updateStateRequest: request,
    });
    return participant;
  } catch (error) {
    console.error('Failed to update participant state:', error);
    throw error;
  }
}
